import { ref } from "vue";

import {
  PreSaleBusinessInformationOutSchema,
  SignUpFlow,
  SignupStage,
  SignupStageOutSchema,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import { getSignUpStage } from "@/api/requests/onboarding";
import { getSubscriptionPlan } from "@/api/requests/purchase";
import router from "@/router";
import { useAuthStore, useSignUpStore, useUsersStore } from "@/stores";

const showSignupResumptionModal = ref(false);

export function useAuthRedirect() {
  const signupStore = useSignUpStore();
  const userStore = useUsersStore();
  const authStore = useAuthStore();
  const isNumberChanged = ref<boolean | undefined>(false);

  const updateUserAndBusinessInfo = (
    fetchRecommendation:
      | SignupStageOutSchema
      | PreSaleBusinessInformationOutSchema
  ) => {
    const preSaleId =
      "pre_sale_id" in fetchRecommendation
        ? fetchRecommendation.pre_sale_id
        : "id" in fetchRecommendation
          ? fetchRecommendation.id
          : null;

    authStore.email = fetchRecommendation.email;
    signupStore.businessName = fetchRecommendation.business_name;
    signupStore.getInternationalNumber =
      !!fetchRecommendation.makes_international_calls;
    signupStore.role = fetchRecommendation.job_title;
    signupStore.hearAboutUs = fetchRecommendation.referral_source;
    signupStore.numberOfReceivers = fetchRecommendation.team_size;
    signupStore.country = fetchRecommendation.address;
    signupStore.industry = fetchRecommendation.sector;
    signupStore.preSaleId = preSaleId;
    signupStore.recommended_plan = signupStore.plans.find(
      (plan) => fetchRecommendation.plan === plan.id
    );

    if ("available_number" in fetchRecommendation) {
      signupStore.selectedNumber = {
        phone_number: fetchRecommendation.available_number || "",
        country_code: fetchRecommendation.country || "",
        provider: fetchRecommendation.provider || "",
      };
    }

    signupStore.is_available_number_changed =
      "is_available_number_changed" in fetchRecommendation
        ? fetchRecommendation.is_available_number_changed
        : false;

    isNumberChanged.value =
      "is_available_number_changed" in fetchRecommendation
        ? fetchRecommendation.is_available_number_changed
        : false;

    signupStore.flowType = (fetchRecommendation.signup_flow ||
      "A") as SignUpFlow;

    userStore.registerUser.first_name = fetchRecommendation.first_name;
    userStore.registerUser.last_name = fetchRecommendation.last_name;
    userStore.registerUser.personal_email = fetchRecommendation.email;
    userStore.registerUser.mobile = fetchRecommendation.mobile;
    userStore.registerUser.id =
      "id" in fetchRecommendation
        ? fetchRecommendation.id
        : "user_id" in fetchRecommendation
          ? fetchRecommendation.user_id
          : null;
  };

  const redirectToNextStageAfterReturn = async (
    currentStage?: SignupStage | null
  ) => {
    // const router = useRouter();
    if (currentStage !== "completed") {
      showSignupResumptionModal.value = true;
    }
    switch (currentStage) {
      case "contact_information":
        signupStore.completedStep = 0;
        if (authStore.isAuthenticated) {
          showSignupResumptionModal.value = false;
          await router.push("/complete-signup");
        }
        break;
      case "business_information":
        if (signupStore.flowType === "B") {
          signupStore.completedStep = 2;
        } else {
          signupStore.completedStep = 1;
        }
        break;
      case "number_selection":
      case "invoice_preview":
        signupStore.completedStep = 2;
        break;
      case "completed":
        signupStore.completedStep = 0;
        if (!authStore.isAuthenticated) {
          await router.push("/email-verification");
        } else {
          await router.push("/complete-signup");
        }
        break;
      default:
        if (authStore.isAuthenticated) {
          await router.push("/complete-signup");
        } else {
          await router.push("/sign-up");
        }
    }
  };

  const handleCurrentSignupStage = async (globalId?: string) => {
    try {
      const decodedGlobalId = atob(globalId ? globalId : signupStore.globalId);

      const [plansResponse, signupResponse] = await Promise.all([
        getSubscriptionPlan({ planType: "number" }),
        getSignUpStage({ globalId: decodedGlobalId }),
      ]);

      signupStore.plans = plansResponse.data.data;

      const signupDetails = signupResponse?.data.data;

      if (signupDetails) {
        authStore.email = signupDetails.email; // update email to trigger verifications
        updateUserAndBusinessInfo(signupDetails);
      }

      signupStore.is_available_number_changed =
        signupDetails?.is_available_number_changed;

      await redirectToNextStageAfterReturn(signupDetails?.stage);
    } catch (error) {
      handleError({ error, silent: true });
    }
  };

  return {
    updateUserAndBusinessInfo,
    redirectToNextStageAfterReturn,
    isNumberChanged,
    handleCurrentSignupStage,
    showSignupResumptionModal,
  };
}
